// @ts-ignore
import { SeriesGalleryServiceFacade } from '@ikea-aoa/ikea-shared-services';
// @ts-ignore
import { UrlUtility } from '@ikea-aoa/ikea-shared-utils';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import masterConstants from '../../settings/masterConstants';
import styles from './ScreenSaver.module.less';
import constants from '../../settings/constants';
import { actionSetScreensaverMode } from '../../state/screensaver/screensaverActions';
import { KompisCountdownBanner } from '@inter-ikea-kompis/react-components';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import { thunkResetApp } from '../../state/init/initThunks';
import { actionOpenSeriesGallery } from '../../state/dialog/dialogActions';
import { thunkSetView } from '../../state/navigation/navigationThunks';
import storage from '../../services/history/storage';
import useScreensaverTimer from './useScreensaverTimer';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import {
  applicationSettings,
  getIpexGalleryUrl,
} from '../../settings/application';
import useModeHandler from './useModeHandler';
import SlideShow from './SlideShow/SlideShow';
import useKeyboard from '../../util/useKeyboard';
import { actionSetSurveyVisible } from '../../state/popups/popupsActions';

export type ScreensaverModes = {
  [key: string]: {
    component: () => JSX.Element | null;
    handlers: {
      onStart?: () => void;
      onEnd?: () => void;
    };
  };
};

const ScreenSaver = () => {
  const kompisTranslations = useSelector(selectKompisTranslations);

  const dispatch = useDispatch();
  const resetApp = () => dispatch(thunkResetApp());
  const setMode = (mode: string) => dispatch(actionSetScreensaverMode(mode));
  const openSeriesGallery = (data: any) =>
    dispatch(actionOpenSeriesGallery(data));
  const goToStart = () =>
    dispatch(thunkSetView(masterConstants.VIEW_NAMES.START));

  const [ignoreIdle, setIgnoreIdle] = React.useState(true);

  const { timeLeft, resetTimer, countdown } = useScreensaverTimer();
  const applicationName = applicationSettings.applicationName.toLowerCase();
  const {
    seriesGallery: { defaultApplication, applications },
  } = UrlUtility.getAppInfo();
  const { SlideShowRenderer, startSlideShow, endSlideShow } = SlideShow();
  const { hideKeyboard } = useKeyboard;

  const SlideShowRef = React.useRef<JSX.Element>(<SlideShowRenderer />);

  // There is an issue with the skapa-sheets for productDetails not closing when screensaver starts
  // When the skapa-sheet is open it is not possible to stop the screensaver
  // This will forcefully remove the skapa-sheet from the DOM when the screensaver starts
  const closeSkapaSheets = () => {
    const visibleModalElement = document.querySelector('skapa-sheet');
    if (visibleModalElement) {
      visibleModalElement.remove();
    }
  };

  /**
   * Handle slideshow mode
   */
  const handleOnStartSlideshowMode = () => {
    hideKeyboard();
    closeSkapaSheets();
    resetApp();
    ipexGalleryShouldBeUsed() && redirectToIpexGallery();
    startScreensaver();
    startSlideShow();
  };

  /**
   * Handle on end slide show
   */
  const handleOnEndSlideshow = () => {
    endSlideShow();
  };

  /**
   * Handles idle mode
   */
  const handleOnEndIdleMode = () => {
    if (ignoreIdle) setIgnoreIdle(false);
    applications.includes(defaultApplication)
      ? goToStart()
      : openSeriesGallery({
          sprGalleryOptions: {
            closable: false,
          },
        });
  };

  /**
   * Redirect to default application
   * @param apps
   */
  const RedirectToDefaultApp = (apps: any) => {
    window.location.href =
      apps
        .find(
          ({ id }: { id: string }) =>
            id.toLowerCase() === defaultApplication.toLowerCase()
        )
        .url.replace(/{vpcCode}/, '') + '&activeScreenSaver=true';
  };

  /**
   * Closes countdown and resets timers
   */
  const closeCountdown = () => {
    setMode(constants.SCREENSAVER_MODE.idle);
    resetTimer();
  };

  /**
   * Start screensaver
   */
  const startScreensaver = () => {
    goToStart();
    dispatch(actionSetSurveyVisible(false));

    if (defaultApplication?.toLowerCase() !== applicationName) {
      // we're not the default app. move away
      resetApp();
      SeriesGalleryServiceFacade.getApplications()
        .then(RedirectToDefaultApp)
        .catch(() => setMode(constants.SCREENSAVER_MODE.slideshow));
    } else {
      // we are the default app. so no series gallery on close
      storage.session.removeItem(constants.SERIES_GALLERY_ACTIVE_KEY);
      setMode(constants.SCREENSAVER_MODE.slideshow);
    }
  };

  /**
   * Handles redirect to ipex gallery
   */
  const redirectToIpexGallery = () =>
    (window.location.href = getIpexGalleryUrl());

  /**
   * Checks if ipex gallery should be used
   */
  const ipexGalleryShouldBeUsed = () => {
    return UrlUtility.getCombinedQuery().ipexGallery === 'true';
  };

  /**
   * We ended up here since BROR was default app and someone else moved
   * here instead of starting its own screensaver
   */
  const handleRedirectFromOtherApp = () =>
    (window.location.hash = window.location.hash
      .replace(/activeScreenSaver[^&?]+&?/, '')
      .replace(/[#&]*$/, ''));

  /**
   * On componentDidMount
   */
  React.useEffect(() => {
    if (UrlUtility.getHashQuery().activeScreenSaver === 'true') {
      handleRedirectFromOtherApp();
      startScreensaver();
    } else {
      resetTimer();
    }
  }, []);

  /**
   * Render countdown
   */
  const renderCountDown = () =>
    countdownWrapper(
      <KompisCountdownBanner
        theme={SkapaTheme}
        translations={kompisTranslations}
        startTime={countdown}
        visible={true}
        showSecondaryButton={ipexGalleryShouldBeUsed()}
        time={timeLeft}
        onClose={closeCountdown}
        onSessionClick={redirectToIpexGallery}
      />
    );

  /**
   * Wrapper for countdown
   * @param child
   */
  const countdownWrapper = (child: JSX.Element | null) => (
    <div className={styles.baseCountdown}>
      <div className={styles.clickTarget}>{child}</div>
    </div>
  );

  /**
   * Wraps slideshow component
   * @param child
   */
  const slideShowWrapper = (child: JSX.Element | null) => (
    <div className={styles.baseSlideshow}>
      <div className={styles.clickTarget}>{child}</div>
    </div>
  );

  /**
   * Render slideshow based on url query param
   */
  const renderSlideShow = () =>
    !ipexGalleryShouldBeUsed() ? slideShowWrapper(SlideShowRef.current) : null;

  const screensaverModes: ScreensaverModes = {
    [constants.SCREENSAVER_MODE.idle]: {
      component: () => null,
      handlers: { onEnd: handleOnEndIdleMode },
    },
    [constants.SCREENSAVER_MODE.countdown]: {
      component: renderCountDown,
      handlers: {},
    },
    [constants.SCREENSAVER_MODE.slideshow]: {
      component: renderSlideShow,
      handlers: {
        onStart: handleOnStartSlideshowMode,
        onEnd: handleOnEndSlideshow,
      },
    },
  };

  const { component } = useModeHandler({
    timeLeft,
    resetTimer,
    countdown,
    screensaverModes,
  });

  return component;
};

export default ScreenSaver;
